import { useState, useEffect } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { ScrollArea } from "../ui/scroll-area";
import { Checkbox } from "../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Utensils,
  ChevronLeft,
  ChevronRight,
  Calendar,
  AlertCircle,
  Leaf,
  Ban,
  Heart,
} from "lucide-react";
import { listStudents, getCantineList, updateStudentRegistry } from "../../lib/api";
import { toast } from "sonner";

const DIETARY_OPTIONS = [
  { value: "sans_porc", label: "Sans porc", icon: Ban, color: "text-orange-600 bg-orange-50" },
  { value: "vegetarien", label: "Végétarien", icon: Leaf, color: "text-green-600 bg-green-50" },
  { value: "sans_gluten", label: "Sans gluten", icon: AlertCircle, color: "text-amber-600 bg-amber-50" },
  { value: "sans_lactose", label: "Sans lactose", icon: AlertCircle, color: "text-blue-600 bg-blue-50" },
  { value: "pai", label: "PAI", icon: Heart, color: "text-rose-600 bg-rose-50" },
];

const DAYS_OF_WEEK = [
  { value: "lundi", label: "Lun" },
  { value: "mardi", label: "Mar" },
  { value: "mercredi", label: "Mer" },
  { value: "jeudi", label: "Jeu" },
  { value: "vendredi", label: "Ven" },
];

export function CantineModule({ onClose }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [cantineList, setCantineList] = useState([]);
  const [editingStudent, setEditingStudent] = useState(null);

  useEffect(() => {
    const fetchStudents = async () => {
      setLoading(true);
      try {
        const response = await listStudents();
        setStudents(response.data || []);
      } catch (err) {
        console.error("Erreur chargement élèves:", err);
        toast.error("Erreur lors du chargement des élèves");
      } finally {
        setLoading(false);
      }
    };
    fetchStudents();
  }, []);

  useEffect(() => {
    const fetchCantine = async () => {
      try {
        const response = await getCantineList(currentDate);
        setCantineList(response.data?.students || []);
      } catch (err) {
        console.error("Erreur chargement cantine:", err);
      }
    };
    if (currentDate) {
      fetchCantine();
    }
  }, [currentDate]);

  const navigateDate = (direction) => {
    const date = new Date(currentDate);
    date.setDate(date.getDate() + (direction === "prev" ? -1 : 1));
    setCurrentDate(format(date, "yyyy-MM-dd"));
  };

  const handleUpdateCantine = async (studentId, cantineInfo) => {
    try {
      await updateStudentRegistry(studentId, { cantine_info: cantineInfo });
      toast.success("Informations mises à jour");
      setEditingStudent(null);
      // Refresh students
      const response = await listStudents();
      setStudents(response.data || []);
    } catch (err) {
      console.error("Erreur mise à jour:", err);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const dayName = format(new Date(currentDate), "EEEE", { locale: fr });
  const formattedDate = format(new Date(currentDate), "d MMMM yyyy", { locale: fr });

  // Count students eating today
  const todayCount = students.filter(s => {
    const info = s.cantine_info || {};
    return info.eats_at_cantine && info.cantine_days?.includes(dayName);
  }).length;

  // Count by dietary restriction
  const dietaryCounts = {};
  students.forEach(s => {
    const restrictions = s.cantine_info?.dietary_restrictions || [];
    restrictions.forEach(r => {
      dietaryCounts[r] = (dietaryCounts[r] || 0) + 1;
    });
    if (s.health_info?.pai) {
      dietaryCounts["pai"] = (dietaryCounts["pai"] || 0) + 1;
    }
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header avec date */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-800">Cantine</h2>
          <p className="text-sm text-slate-500 capitalize">{dayName} {formattedDate}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigateDate("prev")}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Input
            type="date"
            value={currentDate}
            onChange={(e) => setCurrentDate(e.target.value)}
            className="w-auto"
          />
          <Button variant="ghost" size="icon" onClick={() => navigateDate("next")}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-amber-700">{todayCount}</p>
            <p className="text-xs text-amber-600">Inscrits aujourd'hui</p>
          </CardContent>
        </Card>
        {DIETARY_OPTIONS.slice(0, 3).map((opt) => (
          <Card key={opt.value} className={`${opt.color.split(' ')[1]} border-${opt.color.split('-')[1]}-200`}>
            <CardContent className="p-3 text-center">
              <p className={`text-2xl font-bold ${opt.color.split(' ')[0]}`}>
                {dietaryCounts[opt.value] || 0}
              </p>
              <p className={`text-xs ${opt.color.split(' ')[0]}`}>{opt.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Liste des élèves */}
      <ScrollArea className="h-[350px] sm:h-[400px]">
        <div className="space-y-2">
          {students.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Utensils className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>Aucun élève enregistré</p>
            </div>
          ) : (
            students.map((student) => {
              const cantineInfo = student.cantine_info || {};
              const isEditingThis = editingStudent === student.id;
              const eatsToday = cantineInfo.eats_at_cantine && cantineInfo.cantine_days?.includes(dayName);

              return (
                <Card
                  key={student.id}
                  className={`transition-all ${eatsToday ? "border-amber-300 bg-amber-50/50" : ""}`}
                >
                  <CardContent className="p-3">
                    {isEditingThis ? (
                      <EditCantineForm
                        student={student}
                        onSave={(info) => handleUpdateCantine(student.id, info)}
                        onCancel={() => setEditingStudent(null)}
                      />
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                            <span className="text-amber-700 font-medium">
                              {student.student_name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{student.student_name}</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {cantineInfo.eats_at_cantine ? (
                                <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-300">
                                  {cantineInfo.cantine_days?.length || 0} jours
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs text-slate-400">
                                  Non inscrit
                                </Badge>
                              )}
                              {cantineInfo.dietary_restrictions?.map((r) => {
                                const opt = DIETARY_OPTIONS.find(o => o.value === r);
                                return opt ? (
                                  <Badge key={r} variant="outline" className={`text-xs ${opt.color}`}>
                                    {opt.label}
                                  </Badge>
                                ) : null;
                              })}
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingStudent(student.id)}
                        >
                          Modifier
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

function EditCantineForm({ student, onSave, onCancel }) {
  const [info, setInfo] = useState({
    eats_at_cantine: student.cantine_info?.eats_at_cantine || false,
    cantine_days: student.cantine_info?.cantine_days || [],
    dietary_restrictions: student.cantine_info?.dietary_restrictions || [],
  });

  const toggleDay = (day) => {
    setInfo(prev => ({
      ...prev,
      cantine_days: prev.cantine_days.includes(day)
        ? prev.cantine_days.filter(d => d !== day)
        : [...prev.cantine_days, day]
    }));
  };

  const toggleRestriction = (value) => {
    setInfo(prev => ({
      ...prev,
      dietary_restrictions: prev.dietary_restrictions.includes(value)
        ? prev.dietary_restrictions.filter(r => r !== value)
        : [...prev.dietary_restrictions, value]
    }));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Checkbox
          id={`cantine-${student.id}`}
          checked={info.eats_at_cantine}
          onCheckedChange={(checked) => setInfo(prev => ({ ...prev, eats_at_cantine: checked }))}
        />
        <label htmlFor={`cantine-${student.id}`} className="text-sm font-medium">
          Inscrit à la cantine
        </label>
      </div>

      {info.eats_at_cantine && (
        <>
          <div>
            <label className="text-sm font-medium mb-2 block">Jours</label>
            <div className="flex flex-wrap gap-2">
              {DAYS_OF_WEEK.map((day) => (
                <Badge
                  key={day.value}
                  variant={info.cantine_days.includes(day.value) ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => toggleDay(day.value)}
                >
                  {day.label}
                </Badge>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Régimes spéciaux</label>
            <div className="flex flex-wrap gap-2">
              {DIETARY_OPTIONS.filter(o => o.value !== "pai").map((opt) => (
                <Badge
                  key={opt.value}
                  variant={info.dietary_restrictions.includes(opt.value) ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => toggleRestriction(opt.value)}
                >
                  {opt.label}
                </Badge>
              ))}
            </div>
          </div>
        </>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" size="sm" onClick={onCancel}>
          Annuler
        </Button>
        <Button size="sm" onClick={() => onSave(info)}>
          Enregistrer
        </Button>
      </div>
    </div>
  );
}

export default CantineModule;
